import { PrivateBankingServiceDefinitionId } from 'pco-product-service-internal-consumer';
import { ServiceAgreementV2 } from '../eBankingTempTypes';
import { getSafeDepositBoxProductDisplayName } from './safeDepositBoxProductName';

type ServiceAgreementTreeRow = ServiceAgreementV2 & { _path: string[] };

export const BANKING_PRODUCTS = 'Banking products';
export const INVESTMENT_PRODUCTS = 'Investment Products';

export const getProductType = (item: ServiceAgreementV2 | undefined): string =>
  item?.family?.trim().toLowerCase() === 'investment products' ? INVESTMENT_PRODUCTS : BANKING_PRODUCTS;

/**
 * Service model agreements are named after the model they reference, safe deposit boxes after their own
 * configuration, every other product after its definition.
 */
export const getProductDisplayName = (item: ServiceAgreementV2 | undefined): string => {
  switch (item?.serviceDefinitionId) {
    case PrivateBankingServiceDefinitionId.ServiceModel:
      return item.serviceModel?.productDisplayName ?? '';
    case PrivateBankingServiceDefinitionId.SafeDepositBox:
      return getSafeDepositBoxProductDisplayName(item.productSpecificProperties, item.serviceDefinitionName) ?? '';
    default:
      return item?.serviceDefinitionName ?? '';
  }
};

const byProductDisplayName = (a: ServiceAgreementV2, b: ServiceAgreementV2) =>
  getProductDisplayName(a).localeCompare(getProductDisplayName(b));

const toTreeRow = (item: ServiceAgreementV2, productType: string): ServiceAgreementTreeRow => ({
  ...item,
  _path: item.parentAgreement
    ? [productType, item.parentAgreement, item.serviceAgreementId ?? '']
    : [productType, item.serviceAgreementId ?? ''],
});

export function mapServiceAgreementsToTreeRows(items: ServiceAgreementV2[]): ServiceAgreementTreeRow[] {
  const knownIds = new Set(items.map((item) => item.serviceAgreementId));
  const childrenByParent = new Map<string, ServiceAgreementV2[]>();
  const topLevel: ServiceAgreementV2[] = [];

  items.forEach((item) => {
    const parent = item.parentAgreement;

    // a child whose parent is not part of the response has no group to sort within, so it stays top level
    if (parent !== undefined && knownIds.has(parent)) {
      childrenByParent.set(parent, [...(childrenByParent.get(parent) ?? []), item]);
    } else {
      topLevel.push(item);
    }
  });

  // AG Grid keeps the row order it is given per tree level, so ordering here is the grid's default order
  const sortLevel = (level: ServiceAgreementV2[], parentProductType?: string): ServiceAgreementTreeRow[] =>
    [...level].sort(byProductDisplayName).flatMap((item) => {
      const productType = parentProductType ?? getProductType(item);

      return [
        toTreeRow(item, productType),
        ...sortLevel(childrenByParent.get(item.serviceAgreementId ?? '') ?? [], productType),
      ];
    });

  return sortLevel(topLevel);
}
